import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ErrorFlash } from "../components/ErrorFlash";
import { canEditStudent } from "../domain/auth";
import { formatBirthdayLabel, gradeLabel } from "../domain/data";
import {
  mediaReleaseSummary,
  medicalSummary,
  permissionDetailLines,
} from "../domain/permissionForm";
import { displayAnswer, questionLabel } from "../domain/questions";
import {
  formCopyForLocale,
  useForms,
  type FormAnswers,
  type SchoolYearForm,
} from "../state/forms";
import { useAuth } from "../state/AuthProvider";
import { useEvents } from "../state/events";
import { useLocale } from "../state/i18n";
import { useStudents } from "../state/students";
import { GuardianLoginStatus } from "../components/GuardianLoginLookup";
import { ChildSwitcher, PrototypeBanner, StaffNav, StudentNav } from "../components/ui";

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="value">{value}</div>
    </div>
  );
}

function responseLines(
  answers: FormAnswers,
  form: SchoolYearForm,
  locale: "en" | "es",
): { form: Array<{ label: string; value: string }>; extra: Array<{ label: string; value: string }> } {
  const copy = formCopyForLocale(form.copy, locale);
  const permission = permissionDetailLines(answers.permission, locale);
  const consents =
    permission.length > 0
      ? []
      : copy.consents.map((item) => ({
          label: item.label,
          value: answers[item.id] ? (locale === "es" ? "Sí" : "Yes") : "No",
        }));
  const extra = copy.extraQuestions.flatMap((question) => {
    const value = displayAnswer(question, answers.extraAnswers?.[question.id], locale);
    if (value === "-") return [];
    return [{ label: questionLabel(question, locale), value }];
  });
  return { form: [...permission, ...consents], extra };
}

function FormResponse({
  heading,
  lines,
}: {
  heading?: string;
  lines: Array<{ label: string; value: string }>;
}) {
  if (lines.length === 0) return null;
  const body = (
    <div className="grid-2">
      {lines.map((item, index) => (
        <Field key={`${item.label}-${index}`} label={item.label} value={item.value} />
      ))}
    </div>
  );
  if (!heading) return body;
  return (
    <>
      <h2>{heading}</h2>
      <div className="wire dashed">{body}</div>
    </>
  );
}

export function StudentProfile() {
  const { studentId = "mike" } = useParams();
  const navigate = useNavigate();
  const { getStudent, removeStudent } = useStudents();
  const { forms, submissionsForStudent, forgetStudent: forgetForms } = useForms();
  const { forgetStudent: forgetEvents } = useEvents();
  const student = getStudent(studentId);
  const { locale, t } = useLocale();
  const { account } = useAuth();
  const staffView = account?.role === "staff";
  const canEdit = canEditStudent(account, studentId);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");

  if (!student) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <p>{t("student.unknown")}</p>
          <Link to="/">{t("student.back")}</Link>
        </main>
      </div>
    );
  }

  const history = submissionsForStudent(student.id)
    .map((row) => ({
      row,
      form: forms.find((form) => form.id === row.formId),
    }))
    .filter((item) => item.form)
    .sort((a, b) => (a.form!.year < b.form!.year ? 1 : -1));
  const current = history.find((item) => item.form?.status === "open") ?? history[0];
  const answers = current?.row.answers;
  const currentLines =
    answers && current?.form ? responseLines(answers, current.form, locale) : null;
  const past = history.filter((item) => item !== current);

  async function onRemove() {
    if (!student) return;
    setRemoving(true);
    setRemoveError("");
    try {
      await removeStudent(student.id);
      await forgetForms(student.id);
      forgetEvents(student.id);
      navigate("/staff");
    } catch (error) {
      setRemoving(false);
      setRemoveError(error instanceof Error ? error.message : t("profile.removeError"));
    }
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        {staffView ? <StaffNav /> : <StudentNav studentId={student.id} />}
        <ChildSwitcher studentId={student.id} />
        <p className="meta">{t("profile.meta")}</p>
        <h1>
          {student.given} {student.family}
        </h1>
        <p className="lede">{t("profile.lede")}</p>

        {canEdit || staffView ? (
          <div className="actions">
            {canEdit ? (
              <Link className="btn" to={`/student/${student.id}/edit`}>
                {t("profile.edit")}
              </Link>
            ) : null}
            {staffView ? (
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setRemoveError("");
                  setConfirmingRemove((open) => !open);
                }}
              >
                {t("profile.remove")}
              </button>
            ) : null}
          </div>
        ) : null}

        {confirmingRemove ? (
          <div className="wire" role="alertdialog" aria-labelledby="remove-student-title">
            <h2 id="remove-student-title">
              {t("profile.removeTitle", { name: `${student.given} ${student.family}` })}
            </h2>
            <p>{t("profile.removeBody")}</p>
            {removeError ? <ErrorFlash key={removeError} message={removeError} /> : null}
            <div className="actions">
              <button type="button" onClick={() => void onRemove()} disabled={removing}>
                {removing ? t("profile.removing") : t("profile.removeConfirm")}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => setConfirmingRemove(false)}
                disabled={removing}
              >
                {t("profile.removeCancel")}
              </button>
            </div>
          </div>
        ) : null}

        {current?.form ? (
          <div className="wire dashed" role="status">
            <div className="meta">{t("profile.formOnFile")}</div>
            <div className="anno">
              {t("profile.formOnFileLine", {
                form: current.form.name,
                date: current.row.submittedLabel,
              })}
            </div>
          </div>
        ) : null}

        {answers ? (
          <div className="grid-2">
            <div className="wire">
              <div className="meta">{t("profile.medical")}</div>
              <strong>
                {medicalSummary(answers.permission, answers.medical, locale)}
              </strong>
            </div>
            <div className="wire dashed">
              <div className="meta">{t("profile.media")}</div>
              <strong>{mediaReleaseSummary(answers.permission, locale)}</strong>
            </div>
          </div>
        ) : null}

        <div className="wire">
          <div className="grid-2">
            <Field label={t("profile.school")} value={student.school} />
            <Field
              label={t("profile.grade")}
              value={gradeLabel(answers?.grade ?? student.grade)}
            />
            <Field
              label={t("profile.birthday")}
              value={formatBirthdayLabel(answers?.birthday || student.birthday)}
            />
            <Field label={t("profile.age")} value={String(answers?.age ?? student.age)} />
            <Field label={t("profile.studentEmail")} value={student.email} />
          </div>
        </div>

        <h2>{t("profile.guardian")}</h2>
        <div className="wire dashed">
          <div className="grid-2">
            <Field
              label={t("profile.name")}
              value={answers?.guardianName ?? student.guardianName}
            />
            <Field
              label={t("profile.email")}
              value={answers?.guardianEmail ?? student.guardianEmail}
            />
            <Field
              label={t("profile.phone")}
              value={answers?.guardianPhone ?? student.guardianPhone}
            />
          </div>
          {staffView ? (
            <GuardianLoginStatus
              email={answers?.guardianEmail ?? student.guardianEmail}
            />
          ) : null}
        </div>

        <h2>{t("profile.emergency")}</h2>
        <div className="wire dashed">
          <div className="grid-2">
            <Field
              label={t("profile.name")}
              value={answers?.emergencyName ?? student.emergencyName}
            />
            <Field
              label={t("profile.phone")}
              value={answers?.emergencyPhone ?? student.emergencyPhone}
            />
          </div>
        </div>

        {currentLines ? (
          <FormResponse heading={t("profile.permission")} lines={currentLines.form} />
        ) : null}
        {currentLines ? (
          <FormResponse heading={t("profile.extra")} lines={currentLines.extra} />
        ) : null}

        {past.length > 0 ? (
          <>
            <h2>{t("profile.earlier")}</h2>
            {past.map(({ row, form }) => {
              if (!form) return null;
              const lines = responseLines(row.answers, form, locale);
              return (
                <details key={row.id} className="form-year wire dashed">
                  <summary>
                    <strong>{form.name}</strong>
                    <span className="anno">
                      {t("profile.submitted", { date: row.submittedLabel })}
                    </span>
                  </summary>
                  <div className="grid-2">
                    <Field label={t("profile.grade")} value={gradeLabel(row.answers.grade)} />
                    <Field
                      label={t("profile.birthday")}
                      value={formatBirthdayLabel(row.answers.birthday)}
                    />
                    <Field label={t("profile.guardian")} value={row.answers.guardianName} />
                    <Field label={t("profile.email")} value={row.answers.guardianEmail} />
                    <Field label={t("profile.phone")} value={row.answers.guardianPhone} />
                    <Field label={t("profile.emergency")} value={row.answers.emergencyName} />
                    <Field label={t("profile.phone")} value={row.answers.emergencyPhone} />
                    <Field
                      label={t("profile.medical")}
                      value={medicalSummary(row.answers.permission, row.answers.medical, locale)}
                    />
                    <Field
                      label={t("profile.media")}
                      value={mediaReleaseSummary(row.answers.permission, locale)}
                    />
                  </div>
                  <FormResponse lines={lines.form} />
                  <FormResponse lines={lines.extra} />
                </details>
              );
            })}
          </>
        ) : null}

        <div className="actions">
          {staffView ? (
            <Link className="btn ghost" to="/staff">
              Back to schools
            </Link>
          ) : (
            <Link className="btn ghost" to={`/student/${student.id}`}>
              {t("student.backToEvents")}
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}
